/**
 * Scroll Hint
 * - 複数要素対応
 * - HTMX対応
 * - resize対応
 * - ヒント管理は WeakMap を使用
 * - HTMLに id / data属性を追加しない
 * - 自動では消えず、スクロール時のみ消える
 * - 一度スクロールした要素には再表示しない
 */

export function scroll_hint() {
  const MOBILE_MAX = 768;
  const TARGET_SELECTOR = '.is-scroll-on-mobile, .is-scroll-on-pc';

  const hintMap = new WeakMap();
  const scrollHandlerMap = new WeakMap();
  const dismissedTargets = new WeakSet();

  function isTargetActiveForCurrentViewport(target) {
    const isMobile = window.innerWidth <= MOBILE_MAX;

    return isMobile
      ? target.classList.contains('is-scroll-on-mobile')
      : target.classList.contains('is-scroll-on-pc');
  }

  function hasHorizontalScroll(target) {
    if (!target) return false;

    const containerWidth = target.clientWidth;
    const containerScrollWidth = target.scrollWidth;

    if (containerScrollWidth > containerWidth + 1) {
      return true;
    }

    const table = target.querySelector('table');
    if (table) {
      const tableWidth = table.scrollWidth || table.offsetWidth;
      if (tableWidth > containerWidth + 1) {
        return true;
      }
    }

    return false;
  }

  function removeHint(target) {
    const hint = hintMap.get(target);
    if (hint && hint.parentNode) {
      hint.parentNode.removeChild(hint);
    }
    hintMap.delete(target);
  }

  function removeScrollHandler(target) {
    const handler = scrollHandlerMap.get(target);
    if (handler) {
      target.removeEventListener('scroll', handler);
      scrollHandlerMap.delete(target);
    }
  }

  function hideHint(target) {
    const hint = hintMap.get(target);
    if (!hint || !hint.isConnected) {
      hintMap.delete(target);
      return;
    }

    hint.classList.add('is-hidden');

    hint.addEventListener(
      'transitionend',
      () => {
        const currentHint = hintMap.get(target);
        if (currentHint === hint) {
          removeHint(target);
        } else if (hint.parentNode) {
          hint.parentNode.removeChild(hint);
        }
      },
      { once: true }
    );

    // transition がない場合の保険
    setTimeout(() => {
      const currentHint = hintMap.get(target);
      if (currentHint === hint) {
        removeHint(target);
      } else if (hint.parentNode) {
        hint.parentNode.removeChild(hint);
      }
    }, 400);
  }

  function cleanupTarget(target) {
    removeScrollHandler(target);
    removeHint(target);
  }

  function scheduleHintForTarget(target) {
    if (!(target instanceof Element)) return;

    cleanupTarget(target);

    // 一度スクロール済みなら再表示しない
    if (dismissedTargets.has(target)) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!target.isConnected) return;
        if (dismissedTargets.has(target)) return;
        if (!isTargetActiveForCurrentViewport(target)) return;
        if (!hasHorizontalScroll(target)) return;

        const currentPosition = window.getComputedStyle(target).position;
        if (currentPosition === 'static' || !currentPosition) {
          target.style.position = 'relative';
        }

        const hint = document.createElement('div');
        hint.className = 'iwc-scroll-hint';
        hint.textContent = '横にスクロールできます';

        target.appendChild(hint);
        hintMap.set(target, hint);

        let alreadyHidden = false;

        const onScroll = () => {
          if (alreadyHidden) return;
          alreadyHidden = true;

          dismissedTargets.add(target);
          hideHint(target);
          removeScrollHandler(target);
        };

        scrollHandlerMap.set(target, onScroll);
        target.addEventListener('scroll', onScroll, { passive: true });
      }, 0);
    });
  }

  function getTargets(root) {
    const scope = root || document;
    const targets = [];

    if (scope instanceof Element && scope.matches(TARGET_SELECTOR)) {
      targets.push(scope);
    }

    if (scope.querySelectorAll) {
      targets.push(...scope.querySelectorAll(TARGET_SELECTOR));
    }

    return targets;
  }

  function filterNestedTargets(targets) {
    return targets.filter((target) => !target.parentElement?.closest(TARGET_SELECTOR));
  }

  function initAllTargets(root) {
    const targets = filterNestedTargets(getTargets(root));
    targets.forEach((target) => {
      scheduleHintForTarget(target);
    });
  }

  function handleResize() {
    initAllTargets(document);
  }

  function setup() {
    initAllTargets(document);

    document.addEventListener('htmx:afterSwap', (e) => {
      if (e.detail?.target) {
        initAllTargets(e.detail.target);
      }
    });

    document.addEventListener('htmx:afterSettle', (e) => {
      if (e.detail?.target) {
        initAllTargets(e.detail.target);
      }
    });

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup, { once: true });
  } else {
    setup();
  }
}
